import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { useSessionTracker } from "./hooks/useSessionTracker";

// Pages
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import BidsListPage from "./pages/BidsListPage";
import BidDetailPage from "./pages/BidDetailPage";
import NewBidPage from "./pages/NewBidPage";
import UploadPage from "./pages/UploadPage";
import SettingsPage from "./pages/SettingsPage";
import BillingPage from "./pages/BillingPage";
import AuditPage from "./pages/AuditPage";

// Backoffice Pages
import BackofficeDashboard from "./pages/Backoffice/Dashboard";
import BackofficeProspects from "./pages/Backoffice/Prospects";
import BackofficeTemplates from "./pages/Backoffice/Templates";
import BackofficeSettings from "./pages/Backoffice/Settings";
import BackofficeOutbox from "./pages/Backoffice/Outbox";
import BackofficeInbox from "./pages/Backoffice/Inbox";
import BackofficeScout from "./pages/Backoffice/Scout";
import BackofficeSignals from "./pages/Backoffice/Signals";
import BackofficeBeta from "./pages/Backoffice/BetaAdmin";
import BackofficeCompliance from "./pages/Backoffice/CompliancePage";
import FounderDashboard from "./pages/Backoffice/FounderDashboard";
import BillingAdmin from "./pages/Backoffice/BillingAdmin";
import SingleCheckDashboard from "./pages/SingleCheckDashboard";
import BetaPage from "./pages/BetaPage";
import ShareReportPage from "./pages/ShareReportPage";
import TermsPage from "./pages/Legal/TermsPage";
import PrivacyPage from "./pages/Legal/PrivacyPage";
import CookiesPage from "./pages/Legal/CookiesPage";
import DataRequestPage from "./pages/Account/DataRequestPage";
import QuickCheckPage from "./pages/QuickCheckPage";
import SuccessPage from "./pages/SuccessPage";
import CookieBanner from "./components/CookieBanner";
import ConsentGate from "./components/ConsentGate";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  if (!user?.has_org && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (!user?.is_platform_admin) {
    return <Navigate to="/app" replace />;
  }

  return children;
};

// Public Route - redirect to app if already authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated && user?.has_org) {
    if (user.plan === 'pro' || user.is_platform_admin) {
      return <Navigate to="/app" replace />;
    }
    return <Navigate to="/app/checks" replace />;
  }
  
  return children;
};

// Pro-only route — redirects single-credit users to their limited dashboard
const ProRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (user?.is_platform_admin) return children;
  if (!user?.has_org) return <Navigate to="/auth" replace />;
  if (user.plan !== 'pro') return <Navigate to="/app/checks" replace />;
  return children;
};

// Entitlement-aware dashboard router
const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.plan === 'pro' || user?.is_platform_admin) return <ConsentGate><DashboardPage /></ConsentGate>;
  return <SingleCheckDashboard />;
};

// Session tracker for authenticated users
const SessionTracker = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <SessionTrackerInner />;
  }
  return null;
};
const SessionTrackerInner = () => {
  useSessionTracker();
  return null;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      
      {/* Public legal pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />

      {/* Public quick-check wedge */}
      <Route path="/check" element={<QuickCheckPage />} />

      {/* Payment success page */}
      <Route path="/success" element={<SuccessPage />} />

      {/* Public audit intake */}
      <Route path="/audit/:slug" element={<AuditPage />} />
      
      {/* Onboarding - requires auth but no org */}
      <Route path="/onboarding" element={<OnboardingPage />} />
      
      {/* Protected app routes */}
      <Route path="/app" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
      <Route path="/app/checks" element={<ProtectedRoute><SingleCheckDashboard /></ProtectedRoute>} />
      <Route path="/app/bids" element={<ProRoute><ConsentGate><BidsListPage /></ConsentGate></ProRoute>} />
      <Route path="/app/bids/new" element={<ProRoute><ConsentGate><NewBidPage /></ConsentGate></ProRoute>} />
      <Route path="/app/bids/:id" element={<ProRoute><ConsentGate><BidDetailPage /></ConsentGate></ProRoute>} />
      <Route path="/app/upload" element={<ProRoute><ConsentGate><UploadPage /></ConsentGate></ProRoute>} />
      <Route path="/app/settings" element={<ProtectedRoute><ConsentGate><SettingsPage /></ConsentGate></ProtectedRoute>} />
      <Route path="/app/billing" element={<ProtectedRoute><ConsentGate><BillingPage /></ConsentGate></ProtectedRoute>} />
      <Route path="/account/data-request" element={<ProtectedRoute><DataRequestPage /></ProtectedRoute>} />
      
      {/* Backoffice routes - admin only */}
      {/* WORK */}
      <Route path="/backoffice" element={<AdminRoute><BackofficeDashboard /></AdminRoute>} />
      <Route path="/backoffice/prospects" element={<AdminRoute><BackofficeProspects /></AdminRoute>} />
      <Route path="/backoffice/outbox" element={<AdminRoute><BackofficeOutbox /></AdminRoute>} />
      <Route path="/backoffice/inbox" element={<AdminRoute><BackofficeInbox /></AdminRoute>} />
      {/* SCOUT */}
      <Route path="/backoffice/signals" element={<AdminRoute><BackofficeSignals /></AdminRoute>} />
      <Route path="/backoffice/scout" element={<AdminRoute><BackofficeScout /></AdminRoute>} />
      {/* MANAGE */}
      <Route path="/backoffice/dashboard" element={<AdminRoute><FounderDashboard /></AdminRoute>} />
      <Route path="/backoffice/templates" element={<AdminRoute><BackofficeTemplates /></AdminRoute>} />
      <Route path="/backoffice/beta" element={<AdminRoute><BackofficeBeta /></AdminRoute>} />
      <Route path="/backoffice/settings" element={<AdminRoute><BackofficeSettings /></AdminRoute>} />
      {/* Settings sub-tabs (accessible via Settings page, not nav) */}
      <Route path="/backoffice/billing" element={<AdminRoute><BillingAdmin /></AdminRoute>} />
      <Route path="/backoffice/compliance" element={<AdminRoute><BackofficeCompliance /></AdminRoute>} />
      {/* Redirects */}
      <Route path="/backoffice/pipeline" element={<Navigate to="/backoffice/prospects" replace />} />
      <Route path="/backoffice/founder" element={<Navigate to="/backoffice/dashboard" replace />} />
      <Route path="/backoffice/outreach-log" element={<Navigate to="/backoffice/dashboard" replace />} />
      <Route path="/backoffice/audit-inbox" element={<Navigate to="/backoffice/inbox" replace />} />
      <Route path="/backoffice/tasks" element={<Navigate to="/backoffice" replace />} />
      <Route path="/backoffice/executive" element={<Navigate to="/backoffice/dashboard" replace />} />
      <Route path="/backoffice/executive/*" element={<Navigate to="/backoffice/dashboard" replace />} />
      <Route path="/backoffice/system/*" element={<Navigate to="/backoffice/settings" replace />} />
      <Route path="/backoffice/system" element={<Navigate to="/backoffice/settings" replace />} />
      <Route path="/backoffice/support" element={<Navigate to="/backoffice" replace />} />
      
      {/* Public beta page */}
      <Route path="/beta" element={<BetaPage />} />
      
      {/* Public shared report */}
      <Route path="/report/share/:token" element={<ShareReportPage />} />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SessionTracker />
        <AppRoutes />
        <CookieBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
